import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ClippySVG from './ClippySVG';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      navigate('/');
    }
    
    setLoading(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.loginBox}>
        {/* Logo Section */}
        <div style={styles.logoSection}>
          <img 
            src="/gvc-logo.png" 
            alt="GVC School Bus" 
            style={styles.logoImage}
          />
          <h1 style={styles.title}>GVC School Bus</h1>
          <p style={styles.subtitle}>Employee Portal</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="Enter your email"
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              placeholder="Enter your password"
              required
            />
          </div>

          <button 
            type="submit" 
            style={styles.button}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Auth Phase 2 (P2-B, R11): forgot-password entry + the STATIC no-email line (always rendered — no enumeration) */}
        <div style={styles.links}>
          <Link to="/forgot" style={styles.link}>Forgot password?</Link>
          <p style={styles.staticNote}>No email on file? Ask your administrator to reset your password.</p>
        </div>

        {/* Powered by SVT */}
        <div style={styles.poweredBy}>
          Powered by <a href="https://svtconsulting.co" target="_blank" rel="noopener noreferrer" style={styles.svtLink}>SVT Consulting</a>
        </div>
      </div>

      {/* Clippy Mascot */}
      <div style={styles.mascot}>
        <div style={styles.mascotBubble}>
          Welcome! Please sign in to continue. 👋
        </div>
        <ClippySVG size={80} />
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0a0a0a 0%, #1E1E1E 100%)',
    padding: '20px',
    position: 'relative'
  },
  loginBox: {
    background: '#1E1E1E',
    borderRadius: '20px',
    padding: '48px 40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5), 0 0 25px rgba(212, 169, 78, 0.35), 0 0 50px rgba(212, 169, 78, 0.15), 0 0 80px rgba(212, 169, 78, 0.05)',
    border: '1px solid #2A2A2A'
  },
  logoSection: {
    textAlign: 'center',
    marginBottom: '32px'
  },
  logoImage: {
    width: '120px',
    height: 'auto',
    marginBottom: '16px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '4px'
  },
  subtitle: {
    fontSize: '16px',
    color: '#A0A0A0'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#E0E0E0'
  },
  input: {
    padding: '14px 16px',
    fontSize: '16px',
    border: '2px solid #2A2A2A',
    borderRadius: '10px',
    outline: 'none',
    transition: 'border-color 0.2s',
    background: '#141414',
    color: 'white'
  },
  button: {
    padding: '16px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#D4A94E',
    color: '#1a1a1a',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'background-color 0.2s'
  },
  poweredBy: {
    textAlign: 'center',
    marginTop: '32px',
    fontSize: '12px',
    color: '#6B6B6B'
  },
  svtLink: {
    color: '#D4A94E',
    textDecoration: 'none',
    fontWeight: '600'
  },
  links: {
    textAlign: 'center',
    marginTop: '20px'
  },
  link: {
    color: '#D4A94E',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500'
  },
  staticNote: {
    fontSize: '12px',
    color: '#6B6B6B',
    marginTop: '10px',
    marginBottom: 0
  },
  mascot: {
    position: 'fixed',
    bottom: '32px',
    right: '32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end'
  },
  mascotBubble: {
    background: '#1E1E1E',
    padding: '12px 16px',
    borderRadius: '12px',
    marginBottom: '8px',
    fontSize: '14px',
    color: '#E0E0E0',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
    maxWidth: '200px',
    border: '1px solid #2A2A2A'
  }
};

export default Login;
